import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import ReactHtmlParser from "@/src/utils/html-parser";
import { cdnServeUrl } from "@/src/utils/paths";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import Prose from "@/src/components/common/broadsheet/prose";
import SectionHeading from "@/src/components/common/broadsheet/section-heading";
import {
  FONT_TEXT,
  FONT_UI,
  hairline,
  spineForJournal,
} from "@/src/components/common/broadsheet/tokens";
import { overviewEntries } from "@/src/utils/overview-information";

const factLabelSx = {
  fontFamily: FONT_UI,
  fontSize: "0.625rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "custom.inkMuted",
  m: 0,
};

const factValueSx = {
  m: 0,
  fontFamily: FONT_TEXT,
  fontSize: "0.875rem",
  lineHeight: 1.45,
  color: "custom.ink",
  "& a": {
    color: "custom.link",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline", textUnderlineOffset: "0.15em" },
  },
};

const factHtmlSx = {
  ...factValueSx,
  "& > :first-child": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& p": { m: 0 },
  "& p + p": { mt: 0.25 },
  "& ul, & ol": { m: 0, pl: 1.25 },
};

const factsGridSx = {
  m: 0,
  mt: 1,
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(3, minmax(0, 1fr))",
  },
  columnGap: 2.5,
  "& > *": { minWidth: 0 },
};

const FactBlock = ({
  label,
  children,
}: {
  label?: string | null;
  children: React.ReactNode;
}) => (
  <Box sx={{ py: 0.375, ...hairline }}>
    {label?.trim() ? (
      <Typography component="dt" sx={factLabelSx}>
        {label}
      </Typography>
    ) : null}
    <Box component="dd" sx={factHtmlSx}>
      {children}
    </Box>
  </Box>
);

const JournalOverview = ({ journal }: { journal: Journal }) => {
  const meta = journal.meta_data ?? ({} as any);
  const cover = cdnServeUrl(meta.default_cover_image_thumbnail);
  const entries = overviewEntries(meta);

  const organizationLabel =
    journal.options?.template_configs?.journal_overview?.organization?.label ??
    "Organization";
  const frequencyLabel =
    journal.options?.template_configs?.journal_overview?.frequency?.label ??
    "Frequency";

  const standingFacts: { label: string; node: React.ReactNode }[] = [];
  if (journal.organization) {
    standingFacts.push({
      label: organizationLabel,
      node: journal.organization_link ? (
        <NextLink href={journal.organization_link}>
          {journal.organization}
        </NextLink>
      ) : (
        journal.organization
      ),
    });
  }
  if (meta.frequency) {
    standingFacts.push({
      label: frequencyLabel,
      node: (
        <Box component="span" sx={{ textTransform: "capitalize" }}>
          {meta.frequency}
        </Box>
      ),
    });
  }
  if (meta.type_of_publishing) {
    standingFacts.push({
      label: "Publishing",
      node: meta.type_of_publishing,
    });
  }

  return (
    <Box component="section" aria-labelledby="journal-overview-heading">
      <SectionHeading
        title={
          journal.options?.template_configs?.journal_overview?.journal_overview
            ?.label ?? "Journal overview"
        }
        titleId="journal-overview-heading"
        component="h2"
        sx={{ mb: 0.75 }}
      />

      <Box sx={{ display: "flow-root" }}>
        {cover ? (
          <Box
            sx={{
              float: { xs: "none", sm: "right" },
              width: "10rem",
              ml: { sm: 1.25 },
              mb: 0.75,
            }}
          >
            <CoverPlate
              src={cover}
              alt=""
              width={160}
              height={229}
              spine={spineForJournal(journal)}
            />
          </Box>
        ) : null}

        {meta.overview_description ? (
          <Prose sx={{ fontSize: "0.9375rem" }}>
            {ReactHtmlParser(meta.overview_description)}
          </Prose>
        ) : null}

      </Box>

      <Box component="dl" sx={factsGridSx}>
        {standingFacts.map((fact) => (
          <FactBlock key={fact.label} label={fact.label}>
            {fact.node}
          </FactBlock>
        ))}
        {entries.map((entry, index) => (
          <FactBlock key={`entry-${index}`} label={entry.key}>
            {ReactHtmlParser(entry.value as string)}
          </FactBlock>
        ))}
      </Box>
    </Box>
  );
};

export default JournalOverview;
